<template v-if="componentReady">
  <div id="tender-card-input">
    <template v-if="ppiTenders && ppiTenders.length > 0">
      <div id="tenders-account-selection" class="account-selection">
        <p v-if="showAccount" class="iw-body-text-large account-selection__title">
          {{ name }} Account {{ card }}
          <span><app-link class="remove_account___link" :route-to="{}" @click="removeAccountNumber">Remove</app-link></span>
        </p>
        <div v-for="tender in ppiTenders" :key="tender.id" class="tenders__options-radio p-t-10 p-b-10 p-l-10">
          <app-radio-button
            :id="tender.id"
            :label="`${tender.label || tender.type || tender.name} - ${tender.balanceDisplay}`"
            :model-value="tender.id"
            :disabled="validateTenderSelection(tender)"
            group-name="tenderOptions"
            @update:modelValue="handleAccountSelectionChange(tender)"
          />
        </div>
        <div v-if="!token && errorMessage !== ''" class="error-container">
          <img src="../../../../assets/images/warning.svg" class="warning-image" alt="" />
          <p role="alert" class="error-message">{{ errorMessage }}</p>
        </div>
        <div class="account-selection__footer">
          <app-button
            text="Apply"
            :aria-label="'Apply'"
            :handler="applyTender"
            class="account-selection__button"
            :is-disabled="!selection"
            outlined
          />
        </div>
      </div>
    </template>
    <form v-if="!token && (!ppiTenders || ppiTenders.length === 0)" class="tender-card-input">
      <div class="tender-card-input__section tender-card-input__section_column">
        <p class="iw-body-text-large tender-card-input__title">Account Number</p>
        <app-input
          v-model.trim="v$.card.$model"
          :model-value="card"
          class="tender-card-input__input"
          :has-error="v$.card.$error && isFormSubmitted"
          :error-message="errorMessage"
          :accessibility-label="'account number'"
        />
      </div>
      <div v-if="isLoggedIn && isSavedCardSupported() && showSaveAccount" class="ppi__save-wallet m-t-10 m-b-10">
        <label class="checkbox">
          <span class="checkbox__input">
            <input id="save-account" v-model="saveAccount" type="checkbox" name="checkbox" />
            <span class="checkbox__control">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path fill="none" stroke="currentColor" stroke-width="3" d="M1.73 12.91l6.37 6.37L22.79 4.59" class="Path" />
              </svg>
            </span>
          </span>
          <span class="iw-body-text-large ppi__title" for="save-account">Save to my wallet</span>
        </label>
      </div>
      <div class="tender-card-input__footer">
        <app-button
          text="Apply"
          :aria-label="'Apply'"
          :handler="retrieveCardInformation"
          class="tender-card-input__button"
          :is-disabled="isDisabled"
          outlined
        />
      </div>
    </form>
    <div v-if="token && errorMessage !== ''" class="error-container">
      <img src="../../../../assets/images/warning.svg" class="warning-image" alt="" />
      <p role="alert" class="error-message">{{ errorMessage }}</p>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import { CAMPUS_SUBPAYMENT_TYPES, PPI_PAYMENT_TYPE_MEAL } from "@appetize/iw-common/constants";
import { getCardLastFour } from "@/utils/payments";

export default {
  name: "TenderCardInput",
  props: {
    name: String,
    paymentType: Number,
    subpaymentType: Number,
    buttonText: String,
    token: String,
    accountNumber: {
      default: "",
      type: String
    },
    showSaveAccount: {
      default: true,
      type: Boolean
    },
    showAccount: {
      default: true,
      type: Boolean
    }
  },
  setup: () => ({ v$: useVuelidate() }),
  computed: {
    ...mapGetters({
      getAvailableCartMealItems: "cart/getAvailableCartMealItems",
      isLoggedIn: "user/isLoggedIn",
      userData: "user/getUserData"
    }),

    isDisabled() {
      return this.v$.$invalid;
    }
  },
  data() {
    return {
      card: "",
      isFormSubmitted: false,
      errorMessage: "",
      ppiTenders: [],
      selection: undefined,
      selectionType: undefined,
      currency: undefined,
      balances: [],
      reductions: [],
      saveAccount: false,
      componentReady: false
    };
  },
  async mounted() {
    this.card = this.accountNumber;
    if ((this.card && this.card.length > 0) || (this.token && this.token.length > 0)) {
      await this.retrieveCardInformation();
    }
    this.componentReady = true;
  },
  validations: {
    card: {
      required
    }
  },
  methods: {
    ...mapActions({
      ppiGetCardInformation: "cart/ppiGetCardInformation",
      applyPPITender: "cart/applyPPITender",
      saveCard: "user/saveCreditCardToken"
    }),
    isSavedCardSupported() {
      return CAMPUS_SUBPAYMENT_TYPES.includes(parseInt(this.subpaymentType));
    },
    isApplyTenderDisabled() {
      return !this.selection || !this.selectionType;
    },
    handleAccountSelectionChange(tender) {
      this.selection = tender.id;
      this.selectionType = tender.type;
      this.selectionName = tender.name || tender.label;
      this.currency = tender.currency;
      this.balances = tender.balances;
      this.errorMessage = "";
    },
    validateTenderSelection(tender) {
      if (!tender.enabled) {
        return true;
      }
      if (tender.type === PPI_PAYMENT_TYPE_MEAL) {
        return this.getAvailableCartMealItems?.length === 0;
      }

      return !tender.enabled;
    },
    async retrieveCardInformation() {
      this.errorMessage = "";
      try {
        const cardInfo = await this.ppiGetCardInformation({
          tender: {
            paymentType: this.paymentType,
            subpaymentType: this.subpaymentType,
            name: this.name,
            buttonText: this.buttonText
          },
          card: this.card,
          token: this.token
        });
        this.ppiTenders = cardInfo?.ppiTenders || [];
        this.reductions = cardInfo.reductions;
        this.card = cardInfo?.accountNumber || this.card;

        if (!this.ppiTenders || this.ppiTenders.length === 0) {
          this.balances = cardInfo.balances;
          this.applyTender();
        }
      } catch (error) {
        this.errorMessage = error.message;
      }
    },
    removeAccountNumber() {
      this.card = "";
      this.ppiTenders = [];
      this.balances = [];
      this.reductions = [];
      this.selection = undefined;
      this.selectionType = undefined;
      this.selectionName = undefined;
      this.currency = undefined;
      this.errorMessage = "";
      this.saveAccount = false;
    },
    async applyTender() {
      this.errorMessage = "";
      try {
        await this.applyPPITender({
          tender: {
            paymentType: this.paymentType,
            subpaymentType: this.subpaymentType,
            name: this.name,
            buttonText: this.buttonText,
            autoApplied: false,
            selection: this.selection,
            selectionType: this.selectionType,
            selectionName: this.selectionName,
            currency: this.currency
          },
          balances: this.balances,
          reductions: this.reductions,
          card: this.card
        });

        if (this.saveAccount) {
          this.saveCard({
            gatewayId: this.subpaymentType,
            cardToken: this.card,
            cardLast4: getCardLastFour(this.card),
            cardHolderName: `${this.userData?.profile?.firstName} ${this.userData?.profile?.lastName}`
          });
        }
        this.isFormSubmitted = true;
        this.removeAccountNumber();

        //this event will set the checkout checkboxes to default
        this.$emit("cardApplied");
      } catch (error) {
        this.errorMessage = error.message;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.tender-card-input,
.account-selection {
  max-width: 295px;
  font-family: $fontFamilySecondary;
  display: flex;
  flex-direction: column;
  margin: 13px 23px 22px 22px;

  @include respond-to-mobile {
    max-width: none;
  }

  &__input {
    font-size: 14px;
    font-weight: 500;
  }

  &__title {
    font-size: 16px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
  }

  &__section {
    width: 100%;
    display: flex;
    justify-content: space-between;

    &_column {
      flex-direction: column;
    }
  }

  &__footer {
    display: flex;
    width: 100%;
    justify-content: flex-end;
    @include respond-to-mobile {
      border-top: none;
    }
  }
  &__button {
    max-width: 120px;
    height: 40px !important;
  }
}
.error-container {
  display: flex;
  margin-top: -2px;
}

.error-message {
  color: $error-color;
  margin-left: $spacer * 0.5;
  font-weight: normal;
  font-size: 14px;
}

.ppi__save-wallet {
  font-weight: normal;

  input {
    transform: scale(1.25);
  }

  input {
    opacity: 0;
    width: 1em;
    height: 1em;
  }

  label {
    padding-left: 4px;
  }
}

.checkbox__control {
  display: inline-grid;
  width: 22px;
  height: 22px;
  border-radius: 3px;
  border: solid 0.6px rgba(141, 154, 174, 0.4);

  svg {
    margin: auto;
    width: 16px;
    height: 16px;
    border-radius: 1px;
    transform: scale(0);
    transform-origin: bottom left;
  }
}

.checkbox__input {
  display: grid;
  grid-template-areas: "checkbox";

  > * {
    grid-area: checkbox;
  }

  input {
    opacity: 0;
    width: 1em;
    height: 1em;

    &:checked + .checkbox__control svg {
      transform: scale(1);
      color: $group-3-color-1;
      background: $group-3-color-4;
    }

    &:disabled + .checkbox__control svg {
      color: $group-3-color-1;
      background: $group-3-color-3;
    }
  }
}

.checkbox {
  display: grid;
  grid-template-columns: min-content auto;
  grid-gap: 0.5em;
  font-family: Lato, sans-serif;
  font-size: 14px;
  font-stretch: normal;
  font-style: normal;
  color: $group-3-color-2;
  align-items: center;
  &--disabled {
    opacity: 0.4;
  }
}
</style>
